import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigation } from 'react-router'
import { useSpinDelay } from 'spin-delay'
import { cn } from '#app/utils/misc.tsx'

/**
 * The refined-minimal route-pending system (EPT-193), replacing the old
 * perceived-progress crawl. One `useNavigation`-derived pending signal, gated
 * behind a 150ms spin-delay so fast navigations never flash, drives three
 * companion affordances that share a single screen-reader announcement:
 *
 * - the feathered {@link RouteLoadingBar} under the navbar (rendered here),
 * - the stale-content dim + logo halo pulse (consumed via {@link useRoutePending}
 *   inside `AppShell`, which owns both the navbar chrome and the content column),
 * - a `cursor: progress` on `<body>` while pending.
 *
 * The context defaults to `false` so consumers rendered outside a provider (e.g.
 * the root `AppShellBoundary` when the root loader itself failed) read "not
 * pending" rather than needing their own guard.
 */
const RoutePendingContext = React.createContext(false)

/** The shared, spin-delay-gated route-pending flag (`true` once a navigation has
 *  been in flight for 150ms). Read by `AppShell` for the content dim + logo halo. */
export function useRoutePending() {
	return React.useContext(RoutePendingContext)
}

export function RoutePendingProvider({
	children,
	/** Escape hatch for tests — forwarded to `useSpinDelay` (e.g. `{ ssr: false }`
	 *  so the 150ms gate is observable under fake timers). */
	spinDelay,
}: {
	children: React.ReactNode
	spinDelay?: Parameters<typeof useSpinDelay>[1]
}) {
	const navigation = useNavigation()
	const busy = navigation.state !== 'idle'
	// 150ms threshold (EPT-182 motion tokens): no bar/dim/halo on sub-150ms hops.
	const pending = useSpinDelay(busy, { delay: 150, ...spinDelay })

	// Ambient cursor affordance in the fixed chrome (paired with the logo halo).
	React.useEffect(() => {
		if (typeof document === 'undefined') return
		document.body.style.cursor = pending ? 'progress' : ''
		return () => {
			document.body.style.cursor = ''
		}
	}, [pending])

	return (
		<RoutePendingContext.Provider value={pending}>
			<RouteLoadingBar />
			<RoutePendingAnnouncer />
			{children}
		</RoutePendingContext.Provider>
	)
}

/** One feathered brand-gradient comet; the three bar sweeps share it, each
 *  offset by a third of a phase (see below). Dropped under reduced motion. */
const SWEEP =
	'absolute inset-y-0 left-0 w-1/2 bg-[linear-gradient(90deg,transparent,var(--brand),transparent)] motion-safe:animate-route-bar-soft motion-reduce:hidden'

/**
 * Three comets evenly phased across the 2.2s `route-bar-soft` cycle. Negative
 * `animation-delay`s stagger their start positions so — at any moment — at least
 * one comet is mid-track. This is what makes short navigations read smoothly:
 * the sweeps are *always* mounted and populated, so when the bar fades in a
 * comet is already crossing (rather than mounting off-screen at `-100%` and
 * only creeping on well after a ~200ms hop has already resolved), and on resolve
 * the comets fade out *with* the track instead of popping away.
 */
const SWEEPS = [
	{ delay: '0s', className: '' },
	{ delay: '-0.73s', className: 'opacity-80' },
	{ delay: '-1.47s', className: 'opacity-70' },
]

/**
 * The under-navbar loading bar: a 2px `--gray-3` track carrying three evenly
 * phased brand-gradient sweeps (`route-bar-soft`) so the comet stays
 * continuously alive. Keeps the `role="progressbar"` / `aria-valuetext`
 * contract of the bar it replaces. The sweeps run only while pending (paused
 * when idle so they cost nothing between navigations) and are hidden under
 * reduced motion, where the track shows a static brand line instead. Fade in /
 * out on the container (`--duration-fast`).
 */
function RouteLoadingBar() {
	const pending = useRoutePending()
	const { t } = useTranslation()
	return (
		<div
			role="progressbar"
			aria-hidden={pending ? undefined : true}
			aria-label={t('routePending.loading')}
			aria-valuetext={pending ? t('routePending.loading') : undefined}
			className={cn(
				'pointer-events-none fixed inset-x-0 top-15 z-40 h-0.5 overflow-hidden bg-[var(--gray-3)]',
				'transition-opacity duration-[var(--duration-fast)] ease-out',
				// Reduced motion: the track itself becomes the static brand line.
				'motion-reduce:bg-brand',
				pending ? 'opacity-100' : 'opacity-0',
			)}
		>
			{SWEEPS.map((sweep, i) => (
				<span
					key={i}
					aria-hidden
					className={cn(
						SWEEP,
						sweep.className,
						// Freeze the marquee between navigations so it costs nothing
						// while idle; it resumes already populated on the next hop.
						pending ? '' : 'motion-safe:[animation-play-state:paused]',
					)}
					style={{ animationDelay: sweep.delay }}
				/>
			))}
		</div>
	)
}

/**
 * The single visually-hidden `aria-live="polite"` node shared by every pending
 * tier (bar, halo, deferred skeletons). It carries the "Loading" copy only while
 * pending, so it announces exactly once per navigation cycle rather than once per
 * affordance.
 */
function RoutePendingAnnouncer() {
	const pending = useRoutePending()
	const { t } = useTranslation()
	return (
		<output aria-live="polite" className="sr-only">
			{pending ? t('routePending.loading') : ''}
		</output>
	)
}
